import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import {
  API,
  AttributeDTO,
  CompanyAttributeDTO,
  CompanyFullDTO,
  DeviceAttributeDTO,
  DeviceFullDTO,
  LocationAttributeDTO,
  LocationFullDTO,
  ProductAttributeDTO,
  ProductFullDTO,
} from 'src/app/services/api/api';
import { SbModalState } from 'src/app/services/gui/modal/modal-state.service';
import { FreetextResult } from 'src/app/services/gui/modal/modal.model';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from '../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

@Component({
  selector: 'sb-comp-settings',
  templateUrl: './sb-settings.component.html',
  styleUrls: ['./sb-settings.component.scss'],
  standalone: false,
})
export class SbSettingsWindowComponent implements OnInit {
  @Input() modal: string;
  @Input() device: DeviceFullDTO;
  @Input() location: LocationFullDTO;
  @Input() company: CompanyFullDTO;
  @Input() product: ProductFullDTO;
  @Input() heightPx: number;

  constructor(
    private state: SbModalState,
    private api: API,
    private modalService: ModalService,
    public util: UtilService,
    private errorHandler: ErrorService,
    private translate: TranslateService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {}

  get editAllowed(): boolean {
    switch (this.getMode()) {
      case 'DEVICE':
        return this.permissionService.hasPermission(this.permissionService.Permission.device_attributes_edit);
      case 'LOCATION':
        return this.permissionService.hasPermission(this.permissionService.Permission.location_attributes_edit);
      case 'COMPANY':
        return this.permissionService.hasPermission(this.permissionService.Permission.company_attributes_edit);
      case 'PRODUCT':
        return this.permissionService.hasPermission(this.permissionService.Permission.product_attribute_datatype_edit);
      default:
        return false;
    }
  }

  ngOnInit() {
    if (this.modal) {
      this.device = this.state.options.get(this.modal).payload;
      this.location = this.state.options.get(this.modal).payload;
    }
  }

  getMode() {
    if (this.device) {
      return 'DEVICE';
    }
    if (this.location) {
      return 'LOCATION';
    }
    if (this.company) {
      return 'COMPANY';
    }
    if (this.product) {
      return 'PRODUCT';
    }
    return 'DEVICE';
  }

  settingsModal(setting: DeviceAttributeDTO | LocationAttributeDTO | CompanyAttributeDTO | ProductAttributeDTO) {
    const initialSetting: string = setting.value;

    if (!setting.datatype.allowedValues || setting.datatype.allowedValues.length === 0) {
      // no allowed values
      this.modalService
        .freetext({
          initial: initialSetting,
          regex: setting.datatype.forceRegEx,
          unique: setting.datatype.uniqueValues,
          title: this.translate.instant('lang.globals.pleaseEnterAValueFor') + ' "' + setting.name + '".',
        })
        .then((e: FreetextResult<string>) => {
          if (!e.cancel) {
            this.changeSetting(setting, e.value);
          }
        })
        .catch(() => {
          // this.errorHandler.errorHandler(e);
        });
    } else {
      // allowed values
      this.modalService
        .multiSelect<string>({
          initial: initialSetting.split(',').map(value => value.trim()),
          values: setting.datatype.allowedValues,
          title: this.translate.instant('lang.globals.pleaseEnterAValueFor') + ' "' + setting.name + '".',
          modalOptions: { size: 'lg' },
        })
        .then((e: FreetextResult<string[]>) => {
          if (!e.cancel) {
            this.changeSetting(setting, e.value.join(', '));
          }
        })
        .catch(() => {
          // this.errorHandler.errorHandler(e);
        });
    }
  }

  getSettings(): DeviceAttributeDTO[] | LocationAttributeDTO[] | CompanyAttributeDTO[] | ProductAttributeDTO[] {
    switch (this.getMode()) {
      case 'LOCATION':
        return this.location.attributes;
      case 'COMPANY':
        return this.company.attributes;
      case 'PRODUCT':
        return this.product.attributes;
    }

    return this.device ? this.device.attributes : ([] as DeviceAttributeDTO[]);
  }

  changeSetting(setting: DeviceAttributeDTO | LocationAttributeDTO | CompanyAttributeDTO | ProductAttributeDTO, value: string) {
    const oldVal = setting.value;
    setting.value = value;

    switch (this.getMode()) {
      case 'DEVICE':
        this.api
          .ca_md_DeviceAttribute_editAttribute(setting as DeviceAttributeDTO)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: () => {},
            error: error => {
              setting.value = oldVal;
              this.errorHandler.errorHandler(error);
            },
          });
        break;

      case 'LOCATION':
        this.api
          .ca_md_LocationAttribute_editAttribute(setting as LocationAttributeDTO)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: () => {},
            error: error => {
              setting.value = oldVal;
              this.errorHandler.errorHandler(error);
            },
          });
        // this.api.ss_nm_Device_editSetting(this.device.id, setting.datatype.id, setting).subscribe(()=>{});
        break;

      case 'COMPANY':
        this.api
          .ca_md_CompanyAttribute_editAttribute(setting as CompanyAttributeDTO)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: () => {},
            error: error => {
              setting.value = oldVal;
              this.errorHandler.errorHandler(error);
            },
          });
        // this.api.ss_nm_Device_editSetting(this.device.id, setting.datatype.id, setting).subscribe(()=>{});
        break;
      case 'PRODUCT':
        this.api
          .ca_md_ProductAttribute_editAttribute(setting as ProductAttributeDTO)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: () => {},
            error: error => {
              setting.value = oldVal;
              this.errorHandler.errorHandler(error);
            },
          });
        // this.api.ss_nm_Device_editSetting(this.device.id, setting.datatype.id, setting).subscribe(()=>{});
        break;
    }
  }

  sort(e: any[]) {
    let sorted = Object.assign([], e);
    sorted = sorted.sort((a, b) => a.name.localeCompare(b.name));

    return sorted;
  }

  valtoString(e: any) {
    if (e === undefined || e === null) {
      return 'n. def.';
    }
    if (Object.prototype.toString.call(e) === '[object Array]') {
      let s: string = '';
      for (const ele of e) {
        s += this.valtoString(ele) + ', ';
      }

      s = s.substring(0, s.length - 2);

      return s;
    }
    if (e === 'true') {
      return 'JA';
    }
    if (e === 'false') {
      return 'NEIN';
    }
    if (typeof e === 'boolean' && e) {
      return e ? 'JA' : 'NEIN';
    }
    if (e || e === '') {
      if (typeof e === 'string' || typeof e === 'number') {
        return e;
      }
      if (typeof e === 'boolean' && e) {
        return e ? 'JA' : 'NEIN';
      }
      if (e.name) {
        return e.name;
      }
      if (e.id) {
        return e.id;
      }

      return e;
    } else {
      return 'n. def.';
    }
  }
}
